// Renders the configuration an assessment was published with, inside the Edit
// Assessment drawer.
//
// The shape is decided by admin-node's AssessmentConfigService, which returns
// groups of field descriptors ({ key, label, value, type, editable }). Nothing
// here knows what a CEFR level or a resume policy is, and no assessment type is
// named in this file: the editability rules live on the server, where they are
// also enforced on save, so a new assessment type shows up here without a
// frontend change.
//
// Locked fields render as plain text rather than a disabled input — the absence
// of a control, not a colour, is what marks them read-only.

import React from 'react'
import { Alert, Input, InputNumber, Select, Switch, Tag } from 'antd'
import styled from 'styled-components'
import Label from 'components/Form/Label'

const Group = styled.div`
  margin-bottom: 20px;
`

const GroupTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`

const ReadOnlyChip = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 1px 8px;
`

const Field = styled.div`
  margin-bottom: 14px;
`

const ReadOnlyValue = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`

const EmptyValue = styled.span`
  font-size: 13px;
  color: #9ca3af;
`

const Help = styled.div`
  font-size: 11px;
  color: #6b7280;
  line-height: 1.5;
  margin-top: 4px;
`

const Unit = styled.span`
  font-size: 12px;
  color: #6b7280;
  margin-left: 8px;
`

const isEmpty = (value) =>
  value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)

const ReadOnlyField = ({ field, value }) => {
  if (isEmpty(value) && field.type !== 'boolean') {
    return <EmptyValue>Not set</EmptyValue>
  }
  if (field.type === 'boolean') {
    return <ReadOnlyValue>{value ? 'Enabled' : 'Disabled'}</ReadOnlyValue>
  }
  if (field.type === 'list' || field.type === 'tags') {
    return (
      <div>
        {value.map((item) => (
          <Tag key={item} style={{ marginBottom: 4 }}>
            {item}
          </Tag>
        ))}
      </div>
    )
  }
  if (field.type === 'number') {
    return (
      <ReadOnlyValue>
        {value}
        {field.unit ? <Unit>{field.unit}</Unit> : null}
      </ReadOnlyValue>
    )
  }
  return <ReadOnlyValue>{String(value)}</ReadOnlyValue>
}

const EditableField = ({ field, fieldId, value, onChange }) => {
  switch (field.type) {
    case 'boolean':
      return <Switch id={fieldId} checked={!!value} onChange={onChange} />
    case 'textarea':
      return (
        <Input.TextArea
          id={fieldId}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 10 }}
          style={{ fontSize: '13px' }}
        />
      )
    case 'number':
      return (
        <>
          <InputNumber
            id={fieldId}
            value={value === null || value === undefined ? null : value}
            onChange={onChange}
            min={field.min}
            max={field.max}
            style={{ width: 140 }}
          />
          {field.unit ? <Unit>{field.unit}</Unit> : null}
        </>
      )
    case 'select':
      return (
        <Select
          id={fieldId}
          value={value ?? undefined}
          onChange={onChange}
          options={field.options}
          style={{ width: '100%' }}
        />
      )
    case 'tags':
      return (
        <Select
          id={fieldId}
          mode="tags"
          value={value || []}
          onChange={onChange}
          tokenSeparators={[',']}
          placeholder="Type a skill and press Enter"
          style={{ width: '100%' }}
        />
      )
    default:
      return (
        <Input
          id={fieldId}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ fontSize: '13px' }}
        />
      )
  }
}

const AssessmentConfigurationSection = ({ configuration, edits, onFieldChange }) => {
  if (!configuration?.groups?.length) return null

  return (
    <>
      {configuration.contentLocked && configuration.lockedReason && (
        <Alert
          type="info"
          showIcon
          message="This assessment's questions are already generated"
          description={configuration.lockedReason}
          style={{ marginBottom: 20, fontSize: '12px' }}
        />
      )}

      {configuration.groups.map((group) => {
        const isGroupReadOnly = group.fields.every((field) => !field.editable)
        return (
          <Group key={group.key}>
            <GroupTitle>
              <span>{group.title}</span>
              {isGroupReadOnly && <ReadOnlyChip>Read-only</ReadOnlyChip>}
            </GroupTitle>
            {group.fields.map((field) => {
              const fieldId = `assessment-config-${field.key}`
              const value = field.key in edits ? edits[field.key] : field.value
              return (
                <Field key={field.key}>
                  <Label as="label" htmlFor={fieldId}>
                    {field.label}
                  </Label>
                  <div>
                    {field.editable ? (
                      <EditableField
                        field={field}
                        fieldId={fieldId}
                        value={value}
                        onChange={(next) => onFieldChange(field.key, next)}
                      />
                    ) : (
                      <ReadOnlyField field={field} value={value} />
                    )}
                  </div>
                  {field.help && <Help>{field.help}</Help>}
                </Field>
              )
            })}
          </Group>
        )
      })}
    </>
  )
}

export default AssessmentConfigurationSection
